import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { InsulatorCard } from "@/components/InsulatorCard";
import { AdSlot } from "@/components/AdSlot";
import { useAuth } from "@/context/AuthContext";
import { colorHex } from "@/lib/constants";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import { usePageMeta } from "@/lib/usePageMeta";
import { SlidersHorizontal, Plus, X, WifiOff, Search } from "lucide-react";

export default function Catalog() {
  usePageMeta("Insulator Catalog — Browse by CD Number, Maker & Color", "Search antique glass and porcelain insulators by CD number, maker, color, material and rarity. The collector's reference catalog for insulators.");
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ makers: [], colors: [], materials: [], rarities: [] });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [offline, setOffline] = useState(false);

  const search = params.get("search") || "";
  const color = params.get("color") || "";
  const maker = params.get("maker") || "";
  const material = params.get("material") || "";
  const rarity = params.get("rarity") || "";
  const sort = params.get("sort") || "cd";

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => { setSearchInput(search); }, [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput === search) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("search", searchInput); else next.delete("search");
      setParams(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/insulators/meta/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams({ search, color, maker, material, rarity, sort, page_size: "48" });
    try {
      const { data } = await api.get(`/insulators?${qs.toString()}`);
      setData(data);
      setOffline(false);
    } catch {
      setOffline(true);
    } finally {
      setLoading(false);
    }
  }, [search, color, maker, material, rarity, sort]);

  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const activeFilterCount = [color, maker, material, rarity].filter(Boolean).length;
  const recent = getRecentlyViewed();

  const FilterGroup = ({ title, options, param, current, renderSwatch }) => (
    <div className="mb-5">
      <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-2">{title}</p>
      <div className="space-y-1">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`filter-${param}-${o.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
            onClick={() => setParam(param, current === o ? "" : o)}
            className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-colors ${current === o ? "brand-bg text-white" : "hover:bg-black/5"}`}
          >
            {renderSwatch && <span className="w-3 h-3 rounded-full border border-black/10" style={{ background: colorHex(o) }} />}
            {o}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-tech text-xs uppercase tracking-widest brand-text">Reference catalog</p>
          <h1 className="font-display text-4xl font-bold">Insulator Catalog</h1>
          <p className="text-muted-page">{data.total} pieces {search && <>matching “{search}”</>}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-page pointer-events-none" />
            <input
              data-testid="catalog-toolbar-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search CD, maker, name…"
              className="w-56 md:w-64 surface border rounded-full pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
            {searchInput && (
              <button
                data-testid="catalog-toolbar-search-clear"
                onClick={() => setSearchInput("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-page hover:text-current"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {user && (
            <Link to="/contribute" data-testid="contribute-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold surface border hover:shadow">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add an insulator</span><span className="sm:hidden">Add</span>
            </Link>
          )}
          <select data-testid="catalog-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="surface border rounded-full px-4 py-2 text-sm focus:outline-none">
            <option value="cd">Sort: CD Number</option>
            <option value="name">Sort: Name</option>
            <option value="rarity">Sort: Rarity</option>
            <option value="value">Sort: Value</option>
          </select>
          <button data-testid="toggle-filters-btn" onClick={() => setShowFilters((s) => !s)} className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold surface border">
            <SlidersHorizontal className="w-4 h-4" /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
        </div>
      </div>

      {offline && (
        <div data-testid="catalog-offline-banner" className="mb-6 flex items-center gap-2 text-sm rounded-xl px-4 py-2.5 surface border-2" style={{ borderColor: "var(--honey-500)" }}>
          <WifiOff className="w-4 h-4 brand-text shrink-0" /> You're offline — {recent.length > 0 ? "showing the pieces you viewed recently." : "reconnect to browse the full catalog."}
        </div>
      )}

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <div className="surface border rounded-2xl p-4 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display text-xl font-bold">Filters</span>
              {activeFilterCount > 0 && (
                <button data-testid="clear-filters-btn" onClick={clearAll} className="text-xs brand-text flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
              )}
            </div>
            <div className="relative mb-5">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-page pointer-events-none" />
              <input
                data-testid="catalog-filter-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search CD number, maker…"
                className="w-full surface border rounded-lg pl-9 pr-8 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              {searchInput && (
                <button
                  data-testid="catalog-filter-search-clear"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-page hover:text-current"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <FilterGroup title="Color" options={filters.colors} param="color" current={color} renderSwatch />
            <FilterGroup title="Maker" options={filters.makers} param="maker" current={maker} />
            <FilterGroup title="Material" options={filters.materials} param="material" current={material} />
            <FilterGroup title="Rarity" options={filters.rarities} param="rarity" current={rarity} />
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="surface border rounded-2xl aspect-[3/4] animate-pulse" />)}
            </div>
          ) : data.items.length === 0 ? (
            offline && recent.length > 0 ? (
              <div data-testid="recently-viewed-grid">
                <p className="font-tech text-xs uppercase tracking-widest text-muted-page mb-3">Recently viewed · available offline</p>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 stagger">
                  {recent.map((ins) => <InsulatorCard key={ins.id} ins={ins} />)}
                </div>
              </div>
            ) : (
              <div className="text-center py-24 text-muted-page">
                <p className="font-display text-2xl">No insulators found.</p>
                <button onClick={clearAll} className="mt-3 brand-text honey-underline">Clear filters</button>
              </div>
            )
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 stagger" data-testid="catalog-grid">
              {data.items.map((ins) => <InsulatorCard key={ins.id} ins={ins} />)}
            </div>
          )}
          <div className="mt-10"><AdSlot position="catalog" /></div>
        </div>
      </div>
    </div>
  );
}
